import { isEmpty } from 'lodash'
import React from 'react'
import { DealCardCompanyBlurb } from 'scenes/Dashboard/components/DealCardCompanyBlurb'
import { Deal } from 'scenes/Dashboard/data/deals/types'
import { getOrganizationPerformanceIndicators } from 'scenes/Dashboard/data/organizations/helpers'
import { getOrganizationPositions } from 'scenes/Dashboard/data/organizations/selectors'
import { Organization } from 'scenes/Dashboard/data/organizations/types'
import { isString } from 'util'
import { getFormFormattedDate } from 'utils/date'

import { createStyles, Divider, Fade, Theme, Typography } from '@mui/material'
import { withStyles, WithStyles } from '@mui/styles'

import { getAttachmentsFromOrganization } from '../../utils/getters'
import Attachments from './components/Attachments'
import BasicOrganizationInfo from './components/BasicOrganizationInfo'
import { DealInfo } from './components/DealInfo'
import { OrganizationDetailsPositionData } from './components/OrganizationDetailsPositionData'
import OrganizationReferral from './components/OrganizationReferral/OrganizationReferral.component'
import { PerformanceIndicatorsViewList } from './components/PerformanceIndicators/PerformanceIndicatorsViewList.component'
import SectionHeader from './components/SectionHeader'
import { Signals } from './components/Signals'

type FullOrganizationDetailsViewProps = OrganizationDetailsViewProps & WithStyles<typeof styles>

const OrganizationDetailsView: React.SFC<FullOrganizationDetailsViewProps> = (props) => {
  const { classes, organization, deal } = props

  if (!organization) {
    return null
  }

  const attachments = getAttachmentsFromOrganization({ organization })
  const { signalAssessments } = organization
  const lastFundingRoundDataToDisplay = displayLastFundingRoundData(organization)
  const rejectedInvestorsToDisplay = displayRejectedInvestors(classes.description, organization.rejectedInvestors)
  const investorsInCapTableToDisplay = displayInvestorsInCapTable(classes.description, organization.investorsInCapTable)
  const founderNotesToDisplay = displayFounderNotes(classes.description, organization.founderNotes)
  const foundersPositionDataToDisplay = displayFoundersPositionData(organization)
  const performanceIndicators = getOrganizationPerformanceIndicators(organization)

  return (
    <Fade in={true}>
      <div>
        <DealCardCompanyBlurb className={classes.description} organization={organization} />
        <BasicOrganizationInfo organization={organization} append={<Divider className={classes.divider} />} />

        <DealInfo deal={deal} />

        <PerformanceIndicatorsViewList
          performanceIndicators={performanceIndicators}
          otherTractionMetrics={organization.otherTractionMetrics}
        />

        <Signals signals={signalAssessments || []} />

        <SectionHeader text="Founder data" />
        {lastFundingRoundDataToDisplay}
        {rejectedInvestorsToDisplay}
        {investorsInCapTableToDisplay}
        {founderNotesToDisplay}
        {foundersPositionDataToDisplay}

        <Attachments attachments={attachments} />
        <OrganizationReferral organization={organization} />
      </div>
    </Fade>
  )
}

function displayLastFundingRoundData(organization: Organization) {
  const { totalFundingRaised, lastRoundType } = organization
  let { lastRoundClosedOn } = organization
  if (!lastRoundClosedOn && !totalFundingRaised && !lastRoundType) {
    return null
  }

  if (!!lastRoundClosedOn && !isString(lastRoundClosedOn)) {
    lastRoundClosedOn = getFormFormattedDate(lastRoundClosedOn as Date)
  }

  return (
    <>
      {totalFundingRaised && <SectionHeader subHeader text={`Total Funding Raised (USD): ${totalFundingRaised}`} />}
      {lastRoundType && <SectionHeader subHeader text={`Last Round Type: ${lastRoundType}`} />}
      {lastRoundClosedOn && <SectionHeader subHeader text={`Last Round Type: ${lastRoundClosedOn}`} />}
    </>
  )
}

function displayRejectedInvestors(className: string, rejectedInvestors: string | undefined) {
  if (!rejectedInvestors) {
    return null
  }
  return (
    <>
      <SectionHeader subHeader text="Rejected Investors" />

      <div>
        <Typography variant="body2" className={className}>
          {rejectedInvestors}
        </Typography>
      </div>
    </>
  )
}

function displayInvestorsInCapTable(className: string, investorsInCapTable: string | undefined) {
  if (!investorsInCapTable) {
    return null
  }
  return (
    <>
      <SectionHeader subHeader text="Investors In Cap Table" />

      <div>
        <Typography variant="body2" className={className}>
          {investorsInCapTable}
        </Typography>
      </div>
    </>
  )
}

function displayFounderNotes(className: string, founderNotes: string | undefined) {
  if (!founderNotes) {
    return null
  }
  return (
    <>
      <SectionHeader subHeader text="Organization founder Notes" />

      <div>
        <Typography variant="body2" className={className}>
          {founderNotes}
        </Typography>
      </div>
    </>
  )
}

function displayFoundersPositionData(organization: Organization) {
  const positions = getOrganizationPositions(organization)
  if (isEmpty(positions)) {
    return null
  }

  return (
    <>
      <SectionHeader subHeader text="Founder Positions Data" />

      <OrganizationDetailsPositionData positions={positions} />
    </>
  )
}

export type OrganizationDetailsViewProps = {
  children?: React.ReactNode
  deal?: Deal
  organization?: Organization
  organizationHasFundingRounds?: boolean
}

const styles = ({ spacing }: Theme) =>
  createStyles({
    divider: {
      marginTop: spacing(2),
      marginBottom: spacing(2),
    },
    description: {
      whiteSpace: 'pre-line',
      marginBottom: spacing(2),
    },
  })

export default withStyles(styles)(OrganizationDetailsView)
